// src/services/anonymizationProfilesService.js

const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  "Content-Type": "application/json",
});

const BASE_PATH = `${API_BASE_URL}/anonymization/anonymization-profiles`;

/**
 * List current user's profiles
 * Optional filter:
 *   processType: "anonymization" | "pseudonymization"
 */
export const getAnonymizationProfiles = async (processType = null) => {
  const url = new URL(`${BASE_PATH}/`);

  if (processType) {
    url.searchParams.set("process_type", processType);
  }

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to load profiles (status ${response.status})`);
  }

  return await response.json();
};

/**
 * Get a single profile detail
 */
export const getAnonymizationProfileDetail = async (id) => {
  const response = await fetch(`${BASE_PATH}/${id}/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to load profile (status ${response.status})`);
  }

  return await response.json();
};

/**
 * Create a new profile
 * If your serializer allows writing process_type, include it.
 * If it's read-only, just omit it and create via separate endpoint/action.
 */
export const createAnonymizationProfile = async (payload, processType = null) => {
  const body = processType ? { ...payload, process_type: processType } : payload;

  const response = await fetch(`${BASE_PATH}/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to create profile (status ${response.status}): ${text}`);
  }

  return await response.json();
};

/**
 * Update an existing profile (PUT)
 */
export const updateAnonymizationProfile = async (id, payload, processType = null) => {
  const body = processType ? { ...payload, process_type: processType } : payload;

  const response = await fetch(`${BASE_PATH}/${id}/`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to update profile (status ${response.status}): ${text}`);
  }

  return await response.json();
};

/**
 * Partial update (PATCH)
 */
export const patchAnonymizationProfile = async (id, payload, processType = null) => {
  const body = processType ? { ...payload, process_type: processType } : payload;

  const response = await fetch(`${BASE_PATH}/${id}/`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to patch profile (status ${response.status}): ${text}`);
  }

  return await response.json();
};

/**
 * Delete a profile
 */
export const deleteAnonymizationProfile = async (id) => {
  const response = await fetch(`${BASE_PATH}/${id}/`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete profile (status ${response.status})`);
  }

  return true;
};
